import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { AuthShell, Field } from "@/pages/Login";
import { Button } from "@/components/ui/button";

function Forgot() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(data.message);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  if (sent) return <p className="flex gap-2 rounded-xl bg-primary/10 p-3 text-sm" data-testid="forgot-sent"><CheckCircle2 className="h-5 w-5 shrink-0 text-primary" />{sent} Check your inbox and spam folder.</p>;
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
      <Field id="email" label="Email on your account" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
      <Button type="submit" disabled={busy} className="h-12 w-full rounded-full text-base font-semibold" data-testid="forgot-submit-button">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Email me a reset link"}
      </Button>
    </form>
  );
}

function Reset({ token }) {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return setError("Passwords don't match");
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Log in with your new password.");
      navigate("/login");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
      <Field id="password" label="New password (8+ characters)" type="password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" />
      <Field id="confirm" label="Confirm new password" type="password" minLength={8} required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" />
      {error && <p className="rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="reset-error">{error}</p>}
      <Button type="submit" disabled={busy} className="h-12 w-full rounded-full text-base font-semibold" data-testid="reset-submit-button">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save new password"}
      </Button>
    </form>
  );
}

export default function PasswordReset() {
  const [params] = useSearchParams();
  const token = params.get("token");
  return (
    <AuthShell title={token ? "Choose a new password" : "Reset your password"} sub={token ? "Your reset link works once and expires in 30 minutes." : "We'll email a secure reset link to the address on your account."}>
      {token ? <Reset token={token} /> : <Forgot />}
      <p className="mt-6 text-center text-sm text-muted-foreground"><Link to="/login" className="font-semibold text-primary hover:underline" data-testid="back-to-login-link">Back to log in</Link></p>
    </AuthShell>
  );
}
